import { uiPaper } from "shared/ui/uiSurface"
import {
  Cancel,
  CheckCircle,
  Close,
  Edit,
  EventBusy,
  PlayArrow,
  Save,
  Stop,
} from "@mui/icons-material"
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import dayjs, { Dayjs } from "dayjs"
import { FC, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import {
  useGetRecurringOrderByIdQuery,
  useUpdateRecurringOrderMutation,
  useSkipRecurringOrderDateMutation,
  useStopRecurringOrderMutation,
  useResumeRecurringOrderMutation,
  RecurringOrderUpdateInput,
} from "../../apis/appApi"

const RECURRENCE_TYPES = [
  "DAILY",
  "XDAYS",
  "WEEKLY",
  "WORKDAYS",
  "BIWEEKLY",
  "FOURWEEKS",
  "MONTHLY",
] as const

/** Helper: is a recurring order considered "stopped"? */
const isStopped = (endDate: string | null): boolean => {
  if (!endDate) return false
  const today = new Date().toISOString().split("T")[0]
  return endDate.split("T")[0] < today
}

/** Format a recurrence type for display */
const formatFrequency = (
  recurrenceType: string,
  dailyRecurringCriteria: number | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
  const key = `recurringOrders.frequency.${recurrenceType}`
  if (recurrenceType === "XDAYS" && dailyRecurringCriteria) {
    return t(key, { interval: dailyRecurringCriteria })
  }
  return t(key)
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "-"
  try {
    return new Date(dateStr).toLocaleDateString()
  } catch {
    return dateStr
  }
}

/** Order state color mapping */
const stateColorMap: Record<string, "success" | "error" | "warning" | "info" | "default"> = {
  COMPLETED: "success",
  DELIVERED: "success",
  CANCELLED: "error",
  FAILED: "error",
  ON_THE_WAY: "info",
  ON_THE_WAY_TO_CUSTOMER: "info",
  READY_FOR_HAULAGE: "warning",
  TO_PROCESS_PROVIDER_RECURRING: "warning",
  ASSIGNED: "info",
  DRAFT: "default",
}

type Props = {
  recurringOrderId: number
  open: boolean
  onClose: () => void
  onSnackbar: (message: string, severity?: "success" | "error") => void
}

export const RecurringOrderDetailDrawer: FC<Props> = ({
  recurringOrderId,
  open,
  onClose,
  onSnackbar,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: response, isLoading } =
    useGetRecurringOrderByIdQuery(recurringOrderId, { skip: !open })
  const detail = response?.data

  const [updateRecurringOrder] = useUpdateRecurringOrderMutation()
  const [skipDate] = useSkipRecurringOrderDateMutation()
  const [stopRecurring] = useStopRecurringOrderMutation()
  const [resumeRecurring] = useResumeRecurringOrderMutation()

  // Edit state
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState<Partial<RecurringOrderUpdateInput>>({})

  // Dialogs
  const [skipDialogOpen, setSkipDialogOpen] = useState(false)
  const [skipDateValue, setSkipDateValue] = useState<Dayjs | null>(null)
  const [stopDialogOpen, setStopDialogOpen] = useState(false)
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false)

  // Reset edit form when detail changes
  useEffect(() => {
    if (detail && !editing) {
      setEditForm({
        recurrenceType: detail.recurrenceType,
        dailyRecurringCriteria: detail.dailyRecurringCriteria,
        weeklyRecurringCriteria: detail.weeklyRecurringCriteria,
        monthlyRecurringCriteria: detail.monthlyRecurringCriteria,
        desiredArrivalTimeHour: detail.desiredArrivalTimeHour,
        desiredArrivalTimeHourEnd: detail.desiredArrivalTimeHourEnd,
        notes: detail.notes,
        externalReference: detail.externalReference,
      })
    }
  }, [detail, editing])

  const handleStartEdit = useCallback(() => {
    if (detail) {
      setEditForm({
        recurrenceType: detail.recurrenceType,
        dailyRecurringCriteria: detail.dailyRecurringCriteria,
        weeklyRecurringCriteria: detail.weeklyRecurringCriteria,
        monthlyRecurringCriteria: detail.monthlyRecurringCriteria,
        desiredArrivalTimeHour: detail.desiredArrivalTimeHour,
        desiredArrivalTimeHourEnd: detail.desiredArrivalTimeHourEnd,
        notes: detail.notes,
        externalReference: detail.externalReference,
      })
      setEditing(true)
    }
  }, [detail])

  const handleSaveEdit = useCallback(async () => {
    if (!detail) return
    try {
      await updateRecurringOrder({
        id: detail.id,
        body: {
          ...editForm,
          version: (detail as any).version ?? 0,
        } as RecurringOrderUpdateInput,
      }).unwrap()
      setEditing(false)
      onSnackbar(t("recurringOrders.detail.updated"))
    } catch (err: any) {
      onSnackbar(
        err?.data?.message || err?.message || "Update failed",
        "error",
      )
    }
  }, [detail, editForm, updateRecurringOrder, onSnackbar, t])

  const handleSkipDate = useCallback(async () => {
    if (!detail || !skipDateValue) return
    try {
      await skipDate({
        id: detail.id,
        date: skipDateValue.format("YYYY-MM-DD"),
      }).unwrap()
      setSkipDialogOpen(false)
      setSkipDateValue(null)
      onSnackbar(t("recurringOrders.detail.skipped"))
    } catch (err: any) {
      onSnackbar(
        err?.data?.message || err?.message || "Skip failed",
        "error",
      )
    }
  }, [detail, skipDateValue, skipDate, onSnackbar, t])

  const handleStop = useCallback(async () => {
    if (!detail) return
    try {
      const today = new Date().toISOString().split("T")[0]
      await stopRecurring({ id: detail.id, endDate: today }).unwrap()
      setStopDialogOpen(false)
      onSnackbar(t("recurringOrders.detail.stopped"))
    } catch (err: any) {
      onSnackbar(
        err?.data?.message || err?.message || "Stop failed",
        "error",
      )
    }
  }, [detail, stopRecurring, onSnackbar, t])

  const handleResume = useCallback(async () => {
    if (!detail) return
    try {
      await resumeRecurring(detail.id).unwrap()
      setResumeDialogOpen(false)
      onSnackbar(t("recurringOrders.detail.resumed"))
    } catch (err: any) {
      onSnackbar(
        err?.data?.message || err?.message || "Resume failed",
        "error",
      )
    }
  }, [detail, resumeRecurring, onSnackbar, t])

  const stopped = detail ? isStopped(detail.endDate) : false
  const clientName = detail?.client
    ? `${detail.client.firstName} ${detail.client.lastName}`
    : "-"

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{ paper: { ...uiPaper("UI-DLV2-142"), sx: { width: { xs: "100%", sm: 520 } } } }}
      >
        <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" fontWeight={700}>
                {clientName}
              </Typography>
              <Chip
                size="small"
                label={
                  stopped
                    ? t("recurringOrders.stopped")
                    : t("recurringOrders.active")
                }
                color={stopped ? "error" : "success"}
                variant="outlined"
              />
            </Stack>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {isLoading && (
            <Typography color="text.secondary">Loading...</Typography>
          )}

          {detail && (
            <>
              {/* Template Info / Edit Form */}
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ mb: 1 }}
              >
                {t("recurringOrders.detail.templateInfo")}
              </Typography>

              {editing ? (
                <Stack spacing={2} sx={{ mb: 2 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t("recurringOrders.recurrenceType")}</InputLabel>
                    <Select
                      value={editForm.recurrenceType ?? ""}
                      label={t("recurringOrders.recurrenceType")}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          recurrenceType: e.target.value,
                        }))
                      }
                    >
                      {RECURRENCE_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {t(`recurringOrders.frequency.${type}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {editForm.recurrenceType === "XDAYS" && (
                    <TextField
                      size="small"
                      label={t("recurringOrders.interval")}
                      type="number"
                      value={editForm.dailyRecurringCriteria ?? ""}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          dailyRecurringCriteria: e.target.value
                            ? parseInt(e.target.value, 10)
                            : null,
                        }))
                      }
                      slotProps={{ htmlInput: { min: 2 } }}
                    />
                  )}

                  <TextField
                    size="small"
                    label={t("recurringOrders.detail.notes")}
                    multiline
                    rows={2}
                    value={editForm.notes ?? ""}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        notes: e.target.value || null,
                      }))
                    }
                  />

                  <TextField
                    size="small"
                    label={t("recurringOrders.detail.externalReference")}
                    value={editForm.externalReference ?? ""}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        externalReference: e.target.value || null,
                      }))
                    }
                  />

                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Save />}
                      onClick={handleSaveEdit}
                    >
                      {t("recurringOrders.detail.save")}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Cancel />}
                      onClick={() => setEditing(false)}
                    >
                      {t("recurringOrders.detail.cancel")}
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={0.5} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>{t("recurringOrders.frequency")}:</strong>{" "}
                    {formatFrequency(
                      detail.recurrenceType,
                      detail.dailyRecurringCriteria,
                      t,
                    )}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t("recurringOrders.startDate")}:</strong>{" "}
                    {formatDate(detail.startDate)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t("recurringOrders.endDate")}:</strong>{" "}
                    {formatDate(detail.endDate)}
                  </Typography>
                  {detail.address && (
                    <Typography variant="body2">
                      <strong>{t("recurringOrders.address")}:</strong>{" "}
                      {`${detail.address.streetNumber} ${detail.address.streetName}, ${detail.address.city} ${detail.address.postcode}`}
                    </Typography>
                  )}
                  {(detail.desiredArrivalTimeHour ||
                    detail.desiredArrivalTimeHourEnd) && (
                    <Typography variant="body2">
                      <strong>
                        {t("recurringOrders.detail.arrivalWindow")}:
                      </strong>{" "}
                      {detail.desiredArrivalTimeHour ?? "-"} -{" "}
                      {detail.desiredArrivalTimeHourEnd ?? "-"}
                    </Typography>
                  )}
                  {detail.notes && (
                    <Typography variant="body2">
                      <strong>{t("recurringOrders.detail.notes")}:</strong>{" "}
                      {detail.notes}
                    </Typography>
                  )}
                  {detail.externalReference && (
                    <Typography variant="body2">
                      <strong>
                        {t("recurringOrders.detail.externalReference")}:
                      </strong>{" "}
                      {detail.externalReference}
                    </Typography>
                  )}
                </Stack>
              )}

              {/* Action buttons */}
              {!editing && (
                <>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                    {!stopped && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Edit />}
                        onClick={handleStartEdit}
                      >
                        {t("recurringOrders.detail.edit")}
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EventBusy />}
                      onClick={() => setSkipDialogOpen(true)}
                    >
                      {t("recurringOrders.detail.skip")}
                    </Button>
                    {!stopped ? (
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<Stop />}
                        onClick={() => setStopDialogOpen(true)}
                      >
                        {t("recurringOrders.detail.stop")}
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        color="success"
                        startIcon={<PlayArrow />}
                        onClick={() => setResumeDialogOpen(true)}
                      >
                        {t("recurringOrders.detail.resume")}
                      </Button>
                    )}
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                </>
              )}

              {/* Child Orders */}
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ mb: 1 }}
              >
                {t("recurringOrders.detail.childOrders")} (
                {detail.childOrders?.length ?? 0})
              </Typography>

              {detail.childOrders && detail.childOrders.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Reference</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>{t("recurringOrders.status")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.childOrders.map((order) => (
                        <TableRow
                          key={order.id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            onClose()
                            navigate(`/orders/${order.id}`)
                          }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="primary"
                              fontWeight={500}
                            >
                              {order.reference}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(order.deliveryDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={order.state}
                              color={stateColorMap[order.state] ?? "default"}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("recurringOrders.detail.noChildOrders")}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Drawer>

      {/* Skip Date Dialog */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-143") }}
        open={skipDialogOpen}
        onClose={() => setSkipDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("recurringOrders.detail.skip")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("recurringOrders.detail.skipConfirm")}
          </DialogContentText>
          <DatePicker
            label={t("recurringOrders.detail.skipDate")}
            value={skipDateValue}
            onChange={(val) => setSkipDateValue(val)}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipDialogOpen(false)}>
            {t("recurringOrders.detail.cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!skipDateValue}
            onClick={handleSkipDate}
          >
            {t("recurringOrders.detail.skip")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stop Confirmation Dialog */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-144") }}
        open={stopDialogOpen}
        onClose={() => setStopDialogOpen(false)}
      >
        <DialogTitle>{t("recurringOrders.detail.stop")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("recurringOrders.detail.stopConfirm")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopDialogOpen(false)}>
            {t("recurringOrders.detail.cancel")}
          </Button>
          <Button variant="contained" color="error" onClick={handleStop}>
            {t("recurringOrders.detail.stop")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resume Confirmation Dialog */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-145") }}
        open={resumeDialogOpen}
        onClose={() => setResumeDialogOpen(false)}
      >
        <DialogTitle>{t("recurringOrders.detail.resume")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("recurringOrders.detail.resumeConfirm")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResumeDialogOpen(false)}>
            {t("recurringOrders.detail.cancel")}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleResume}
          >
            {t("recurringOrders.detail.resume")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
